import {
  ModalBody,
  VStack,
  Text,
  Code,
  Button,
  HStack,
  Box,
  IconButton,
  useClipboard,
  useToast,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import ChevronRightSharpIcon from '@gamma/ui/icons/ChevronRightSharpIcon';
import ContentCopySharpIcon from '@gamma/ui/icons/ContentCopySharpIcon';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { KeyFindingsHeader } from './KeyFindingsView';
import type { KeyFinding } from '@investigator/shared/generated/graphql';
import { EvidenceSource } from '@investigator/shared/generated/graphql';
import { useEntityAgenticTriageReportContext } from '../../../context';
import { useEntitiesContext } from '../../../context';
import { useGetTriageEvidenceDetails } from '../../../queries';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { i18n } from '@investigator/shared/localization';
import { SimpleCaretPagination } from '@investigator/shared/components';
import { ROUTES } from '@investigator/shared/constants';
import { formatJSONPreserveOrder } from '@investigator/shared/utils/formatJSON';
import { safeParse } from 'secure-json-parse';
import moment from 'moment';
import { useParseBackticks } from '@investigator/shared/hooks';

const modalCopy = i18n.pages.entities.entityAnalysisModal.keyFindings;

export const KeyFindingsDetailView: React.FC = () => {
  const [evidencePage, setEvidencePage] = useState<number>(0);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const codeBoxRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const { report } = useEntityAgenticTriageReportContext();
  const {
    selectedEntity,
    analysisModal: {
      modalNavigation: { selectedFindingIndex },
    },
  } = useEntitiesContext();
  const { parseBackticks } = useParseBackticks();

  // Specify widget type for pivot query to ensure load
  const pivotQueryWidgetType = '&widgetType=auto';

  // Color mode values for gradient and text
  const gradientOverlay = useColorModeValue(
    'linear(to-t, rgba(255, 255, 255, 0.9), transparent)', // light mode - white
    'linear(to-t, rgba(0, 0, 0, 0.9), transparent)', // dark mode - black
  );
  const keyColor = useColorModeValue('blue.600', 'blue.300');
  const valueColor = useColorModeValue('green.700', 'green.300');
  const copyIconColor = useColorModeValue('gray.700', 'white');

  // Get the selected finding based on selectedFindingIndex
  const selectedFinding: KeyFinding | null = useMemo(() => {
    if (
      !report?.key_findings_info?.key_findings ||
      selectedFindingIndex === null
    ) {
      return null;
    }

    const findings = report.key_findings_info.key_findings.filter(
      (f: KeyFinding | null): f is NonNullable<typeof f> => f !== null,
    );
    const finding = findings[selectedFindingIndex];

    if (!finding) {
      return null;
    }

    return finding;
  }, [report, selectedFindingIndex]);

  const currentEvidence = selectedFinding?.evidences?.[evidencePage] ?? null;

  const {
    data: evidenceDetailsData,
    loading: evidenceLoading,
    error: evidenceError,
  } = useGetTriageEvidenceDetails({
    variables: {
      tenant: selectedEntity?.tenantInfo?.tenant_id ?? '',
      evidence: {
        agent: currentEvidence?.agent ?? '',
        entity_triage_id: currentEvidence?.entity_triage_id ?? '',
        source: currentEvidence?.source ?? EvidenceSource.Tool,
        tool_name: currentEvidence?.tool_name ?? '',
        detection_id: currentEvidence?.detection_id ?? undefined,
      },
    },
    skip:
      !currentEvidence ||
      !selectedEntity?.tenantInfo?.tenant_id ||
      !currentEvidence.agent ||
      !currentEvidence.entity_triage_id,
  });

  const evidenceDetails = evidenceDetailsData?.getTriageEvidenceDetails;
  const evidenceEntries = useMemo(() => {
    if (!evidenceDetails?.evidence) return [];

    try {
      // Parse the JSON string from backend preserving key order
      const parsedMap = formatJSONPreserveOrder(evidenceDetails.evidence);
      const entries = Array.from(parsedMap.entries());

      // Detect if this is an array (all keys are numeric sequential starting from 0)
      const isArray = entries.every(([key], index) => key === String(index));

      return entries.map(([key, value], index) => {
        // For arrays, format the nested JSON and use 1-based indexing
        if (isArray) {
          let formattedValue = value;
          try {
            const parsed = safeParse(value);
            formattedValue = JSON.stringify(parsed, null, 2);
          } catch {
            // If parsing fails, keep original value
            formattedValue = value;
          }
          return {
            key: `${modalCopy.entry} ${index + 1}`,
            value: formattedValue,
            isArrayEntry: true,
          };
        }

        // For objects, keep original key-value pairs
        return { key, value, isArrayEntry: false };
      });
    } catch (error) {
      console.error('Failed to parse evidence:', error);
      return [];
    }
  }, [evidenceDetails]);

  // Prepare formatted text matching the code block display for clipboard
  const formattedEvidenceText = useMemo(() => {
    if (evidenceEntries.length === 0) return '';

    const lines = ['{'];
    evidenceEntries.forEach(({ key, value, isArrayEntry }) => {
      if (isArrayEntry) {
        // For arrays: key on one line, value on next (indented)
        lines.push(`  ${key}:`);
        // Indent multi-line values
        const valueLines = value.split('\n');
        valueLines.forEach((line) => {
          lines.push(`    ${line}`);
        });
      } else {
        // For objects: key and value on same line
        lines.push(`  ${key}: ${value}`);
      }
    });
    lines.push('}');

    return lines.join('\n');
  }, [evidenceEntries]);

  const { onCopy } = useClipboard(formattedEvidenceText);

  // Check if content is overflowing to conditionally show gradient
  useEffect(() => {
    const checkOverflow = () => {
      if (codeBoxRef.current) {
        const hasOverflow =
          codeBoxRef.current.scrollHeight > codeBoxRef.current.clientHeight;
        setIsOverflowing(hasOverflow);
      }
    };

    checkOverflow();
  }, [evidenceEntries, evidenceLoading]);

  const handleCopyEvidence = () => {
    onCopy();
    toast({
      position: 'top',
      status: 'success',
      description: modalCopy.copySuccess,
    });
  };

  const artifactLabel = useMemo(() => {
    return (
      currentEvidence?.tool_name ||
      i18n.formatString(modalCopy.entryN, evidencePage + 1)
    );
  }, [currentEvidence?.tool_name, evidencePage]);

  if (!selectedFinding) {
    return null;
  }
  return (
    <>
      <KeyFindingsHeader />
      <ModalBody overflowY="auto" flex="1" p={6}>
        <VStack width="100%" spacing={2} align="stretch">
          <Text textStyle="body-md" fontWeight="bold">
            {parseBackticks(selectedFinding.title || '')}
          </Text>
          <Text>{parseBackticks(selectedFinding.justification || '')}</Text>
        </VStack>
        <VStack spacing={3} align="start" marginBottom={2} marginTop={6}>
          <HStack alignItems="center">
            <Text fontWeight="bold">{modalCopy.associatedEvidence} </Text>
            {selectedFinding.evidences &&
              selectedFinding.evidences.length > 0 && (
                <SimpleCaretPagination
                  currentPage={evidencePage}
                  pageLength={selectedFinding.evidences.length}
                  setCurrentPage={setEvidencePage}
                  buttonProps={{
                    border: '1px solid transparent',
                    sx: {
                      '&:hover:not(:disabled)': {
                        borderColor: 'border.default',
                        bg: 'transparent',
                        color: 'text.primary',
                      },
                    },
                  }}
                />
              )}
          </HStack>
          {selectedFinding.evidences && selectedFinding.evidences.length > 0 ? (
            <Box position="relative" width="100%">
              {evidenceDetails?.evidence && (
                <IconButton
                  position="absolute"
                  top="10px"
                  right="20px"
                  aria-label="copy evidence button"
                  icon={<ContentCopySharpIcon boxSize={4} />}
                  variant="ghost"
                  size="sm"
                  zIndex={2}
                  onClick={handleCopyEvidence}
                  color={copyIconColor}
                />
              )}
              <Code
                ref={codeBoxRef}
                bg="layer.0"
                overflowY="auto"
                maxHeight="400px"
                paddingTop={4}
                paddingBottom={4}
                paddingLeft={4}
                paddingRight={12}
                width="100%"
                display="block"
              >
                {evidenceLoading || evidenceError ? (
                  <GraphQLReqStatus
                    loading={evidenceLoading}
                    error={evidenceError}
                  />
                ) : evidenceEntries.length === 0 ? (
                  <Text color="text.secondary">
                    {i18n.formatString(
                      modalCopy.noEvidenceDataAvailableFor,
                      String(artifactLabel),
                    )}
                  </Text>
                ) : (
                  <VStack align="start" spacing={1}>
                    <Text>{'{'}</Text>
                    {evidenceEntries.map(
                      ({ key, value, isArrayEntry }, index) =>
                        isArrayEntry ? (
                          <VStack
                            key={`${key}-${index}`}
                            spacing={1}
                            align="start"
                          >
                            <Text color={keyColor} fontWeight="medium">
                              {key}:
                            </Text>
                            <Text
                              color={valueColor}
                              wordBreak="break-word"
                              whiteSpace="pre-wrap"
                            >
                              {value}
                            </Text>
                          </VStack>
                        ) : (
                          <HStack
                            key={`${key}-${index}`}
                            spacing={1}
                            align="start"
                          >
                            <Text
                              color={keyColor}
                              fontWeight="medium"
                              minW="150px"
                              flexShrink={0}
                            >
                              {key}:
                            </Text>
                            <Text
                              color={valueColor}
                              wordBreak="break-word"
                              whiteSpace="pre-wrap"
                            >
                              {value}
                            </Text>
                          </HStack>
                        ),
                    )}
                    <Text>{'}'}</Text>
                  </VStack>
                )}
              </Code>
              {isOverflowing && (
                <Box
                  data-testid="evidence-gradient-overlay"
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  height="80px"
                  bgGradient={gradientOverlay}
                  pointerEvents="none"
                  zIndex={1}
                />
              )}
            </Box>
          ) : (
            <Text color="text.secondary">{modalCopy.noEvidenceAvailable}</Text>
          )}
        </VStack>
        <Tooltip
          label={modalCopy.queryUnavailable}
          isDisabled={!!evidenceDetails?.pivot_query}
          placement="top"
        >
          <Button
            variant="solid"
            colorScheme="gray"
            rightIcon={<ChevronRightSharpIcon />}
            isDisabled={!evidenceDetails?.pivot_query}
            onClick={() => {
              if (evidenceDetails?.pivot_query) {
                const end = moment().unix();
                const start = moment().subtract(7, 'days').unix();
                const humioStart = start * 1000;
                const humioEnd = end * 1000;

                const url = ROUTES.logSearchWithParams(
                  `${pivotQueryWidgetType}&start=${start}&end=${end}&humioStart=${humioStart}&humioEnd=${humioEnd}&query=${encodeURIComponent(evidenceDetails.pivot_query)}`,
                );
                window.open(url, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            {modalCopy.viewInLogSearch}
          </Button>
        </Tooltip>
      </ModalBody>
    </>
  );
};
